export const DEFAULT_LOCALE = "en" as const;
export const WORKSPACE_LOCALE_COOKIE = "workspaceLocale" as const;

export const SUPPORTED_LOCALES = ["en", "es", "fr", "de", "it", "pt"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALIZED_LOCALES = SUPPORTED_LOCALES.filter(locale => locale !== DEFAULT_LOCALE);

const localizedLocaleSet = new Set<string>(LOCALIZED_LOCALES);
const supportedLocaleSet = new Set<string>(SUPPORTED_LOCALES);
const LOCALE_TO_TRANSCRIPTION_LANGUAGE: Record<SupportedLocale, string> = {
  en: "en",
  es: "es",
  fr: "fr",
  de: "de",
  it: "it",
  pt: "pt",
};

export function isSupportedLocale(value: string): value is SupportedLocale {
  return supportedLocaleSet.has(value);
}

export function isLocalizedLocale(value: string): value is SupportedLocale {
  return localizedLocaleSet.has(value);
}

export const LOCALE_TO_OG_LOCALE: Record<SupportedLocale, string> = {
  en: "en_US",
  es: "es_ES",
  fr: "fr_FR",
  de: "de_DE",
  it: "it_IT",
  pt: "pt_BR",
};

export const LOCALE_TO_DATE_LOCALE: Record<SupportedLocale, string> = {
  en: "en-US",
  es: "es-ES",
  fr: "fr-FR",
  de: "de-DE",
  it: "it-IT",
  pt: "pt-BR",
};

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: "English",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  pt: "Português",
};

export const LOCALE_FLAGS: Record<SupportedLocale, string> = {
  en: "🇬🇧",
  es: "🇪🇸",
  fr: "🇫🇷",
  de: "🇩🇪",
  it: "🇮🇹",
  pt: "🇵🇹",
};

export type UserLocaleValue = "english" | "spanish" | "italian" | "french" | "german" | "portugese" | "estonian" | "dutch";

const USER_LOCALE_TO_SUPPORTED: Record<UserLocaleValue, SupportedLocale | null> = {
  english: "en",
  spanish: "es",
  italian: "it",
  french: "fr",
  german: "de",
  portugese: "pt",
  estonian: null,
  dutch: null,
};

const SUPPORTED_LOCALE_TO_USER: Record<SupportedLocale, Exclude<UserLocaleValue, "estonian" | "dutch">> = {
  en: "english",
  es: "spanish",
  fr: "french",
  de: "german",
  it: "italian",
  pt: "portugese",
};

export function getSupportedLocaleFromUserLocale(value?: string | null): SupportedLocale {
  if (!value) {
    return DEFAULT_LOCALE;
  }
  const normalized = value.toLowerCase() as UserLocaleValue;
  return USER_LOCALE_TO_SUPPORTED[normalized] ?? DEFAULT_LOCALE;
}

export function getUserLocaleFromSupportedLocale(locale: SupportedLocale): UserLocaleValue {
  return SUPPORTED_LOCALE_TO_USER[locale];
}

export function getWorkspaceLocaleFromCookieValue(value?: string | null): SupportedLocale | null {
  if (!value) {
    return null;
  }

  const normalized = value.trim();
  return isSupportedLocale(normalized) ? normalized : null;
}

export function getWorkspaceLocaleFromCookieString(cookieString?: string | null): SupportedLocale | null {
  if (!cookieString) {
    return null;
  }

  const cookieEntry = cookieString
    .split(";")
    .map(entry => entry.trim())
    .find(entry => entry.startsWith(`${WORKSPACE_LOCALE_COOKIE}=`));

  if (!cookieEntry) {
    return null;
  }

  return getWorkspaceLocaleFromCookieValue(cookieEntry.slice(WORKSPACE_LOCALE_COOKIE.length + 1));
}

export function getDefaultTranscriptionLanguageForLocale(locale: SupportedLocale): string {
  return LOCALE_TO_TRANSCRIPTION_LANGUAGE[locale];
}

export function withLocalePath(locale: SupportedLocale, path: string): string {
  if (!path.startsWith("/")) {
    throw new Error(`Path must start with "/". Received: ${path}`);
  }

  if (locale === DEFAULT_LOCALE) {
    return path;
  }

  return path === "/" ? `/${locale}` : `/${locale}${path}`;
}

export function buildLanguageAlternates(path: string): Record<string, string> {
  return SUPPORTED_LOCALES.reduce<Record<string, string>>((acc, locale) => {
    acc[locale] = withLocalePath(locale, path);
    return acc;
  }, {});
}

export function getLocaleFromPathname(pathname: string): SupportedLocale {
  const segment = pathname.split("/")[1];
  if (segment && isLocalizedLocale(segment)) {
    return segment;
  }
  return DEFAULT_LOCALE;
}

export function stripLocaleFromPathname(pathname: string): string {
  const parts = pathname.split("/");
  if (parts[1] && isLocalizedLocale(parts[1])) {
    const rest = `/${parts.slice(2).join("/")}`;
    return rest === "/" ? "/" : rest.replace(/\/$/, "");
  }
  return pathname === "" ? "/" : pathname;
}
